/**
 * One section of an open record, as an accordion item (2026-10-30, owner: the UMED record was "a long
 * undifferentiated stack" where "empty states shout as loudly as data").
 *
 * Why an accordion and not tabs: an operator works a case top to bottom and cross-checks as they go (the DCF account
 * while sending the packet, the approval evidence while reading the notes). Tabs would show one section at a time and
 * hide the rest; an accordion keeps several open at once, lets the empty ones fold to a single quiet line, and keeps
 * the board's existing jumps (a move that lands "at the packet" or "at the approval") a scroll, not a tab switch.
 *
 *   - The header is an h3 holding one button (aria-expanded / aria-controls): Enter or Space toggles it, and
 *     Up / Down / Home / End move between the headers of one RecordSections group (the WAI-ARIA accordion pattern).
 *   - A count badge (muted) and a one-line summary say what is inside while it is folded.
 *   - Folded content stays MOUNTED, hidden by the stylesheet (`data-state="closed"`), so a half-typed note or a form a
 *     board act opened is never thrown away, and nothing inside re-fetches when the section opens again.
 *   - Opening slides the content in (recordSection.css); none under reduced motion.
 */
import React, { useCallback, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import './recordSection.css';

export interface RecordSectionProps {
  /** A DOM id, unique on the page: the section, its button and its body derive theirs from it. */
  id: string;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A muted count beside the title (documents, notes, events). Omitted: no badge. */
  count?: number | null;
  /** What the count counts, for a screen reader ("documents"). */
  countLabel?: string;
  /** One quiet line after the title, e.g. "None yet". */
  summary?: string | null;
  /** Nothing recorded here: the header reads quieter. */
  empty?: boolean;
  className?: string;
  children: React.ReactNode;
}

export function RecordSection({ id, title, open, onOpenChange, count = null, countLabel, summary = null, empty = false, className = '', children }: RecordSectionProps) {
  // Motion only for an opening the person asked for, never on the first paint of a record.
  const [toggled, setToggled] = useState(false);
  const triggerId = `${id}-trigger`;
  const hasCount = count !== null && count !== undefined;
  const said = `${hasCount ? `, ${count}${countLabel ? ` ${countLabel}` : ''}` : ''}${summary ? `, ${summary}` : ''}`;
  const bodyId = `${id}-body`;
  return (
    <section id={id} className={`rs${empty ? ' rs--empty' : ''}${className ? ` ${className}` : ''}`} data-state={open ? 'open' : 'closed'}
      data-toggled={toggled ? 'true' : undefined} aria-labelledby={triggerId}>
      <h3 className="rs-head">
        <button type="button" id={triggerId} className="rs-trigger" aria-expanded={open} aria-controls={bodyId}
          onClick={() => { setToggled(true); onOpenChange(!open); }}>
          <ChevronRight className="rs-chev" aria-hidden="true" size={16} strokeWidth={2} />
          {/* One name for a screen reader ("Notes, 0 notes, None yet"); the badge and the quiet line are for the eye. */}
          <span className="rs-sr">{`${title}${said}`}</span>
          <span className="rs-title" aria-hidden="true">{title}</span>
          {hasCount && <span className="rs-count" aria-hidden="true">{count}</span>}
          {summary ? <span className="rs-summary" aria-hidden="true">{summary}</span> : null}
        </button>
      </h3>
      <div id={bodyId} className="rs-body">{children}</div>
    </section>
  );
}

/** A group of RecordSections: arrow keys move between their headers. */
export function RecordSections({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const t = e.target as HTMLElement;
    // Alt+Up / Alt+Down step through the board's records (the dock's own shortcut): not ours.
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    if (!t.classList || !t.classList.contains('rs-trigger') || !ref.current) return;
    // Only this group's own headers (a nested group keeps its own).
    const own = Array.from(ref.current.querySelectorAll<HTMLButtonElement>('.rs-trigger')).filter((b) => b.closest('.rs-group') === ref.current);
    const at = own.indexOf(t as HTMLButtonElement);
    if (at < 0) return;
    let next = -1;
    if (e.key === 'ArrowDown') next = (at + 1) % own.length;
    else if (e.key === 'ArrowUp') next = (at - 1 + own.length) % own.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = own.length - 1;
    if (next < 0) return;
    e.preventDefault();
    own[next].focus();
  }, []);
  return (
    // The keydown listener only routes arrow keys between the section buttons inside; the buttons are the controls.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={`rs-group${className ? ` ${className}` : ''}`} ref={ref} onKeyDown={onKeyDown}>
      {children}
    </div>
  );
}

export default RecordSection;
